import {
  defaultCheck,
  greenCheck,
  hidepassword,
  loginBg,
  showpassword,
} from "@/constants";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/services/api.service";
import { toast } from "react-toastify";
import { Input } from "@/components/ui/input";
import { Navigate, useNavigate } from "react-router-dom";
import { Checkbox } from "@nextui-org/react";

interface PasswordValidationResults {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  specialChar: boolean;
}

interface Step2Props {
  email: string;
  token: string;
  onSave: () => void;
}

const passwordCriteria = {
  length: "8-12 character",
  uppercase: "1 UPPER CASE letter",
  lowercase: "1 lower case letter",
  specialChar: "1 special character",
};

const passwordSchema = z
  .string()
  .min(8, "8-12 character")
  .max(12, "8-12 character")
  .regex(/[A-Z]/, "1 UPPER CASE letter")
  .regex(/[a-z]/, "1 lower case letter")
  .regex(/[\W_]/, "1 special character");

// Define the validation schema using Zod
const signupSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"], // This is where the error will be attached in the error object
  });

// Define the form inputs' type
type SignUpFormData = z.infer<typeof signupSchema>;

// interface PasswordResetStep2 {
//   setStep: React.Dispatch<React.SetStateAction<number>>;
// }

export const Step2 = ({ email, token, onSave }: Step2Props) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signupSchema),
  });
  const [passwordStrengthLevel, setPasswordStrengthLevel] = useState(0);
  const [passwordValidationResults, setPasswordValidationResults] =
    useState<PasswordValidationResults>({
      length: false,
      uppercase: false,
      lowercase: false,
      specialChar: false,
    });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [logoutAllDevices, setLogoutAllDevices] = useState(false);

  const password = watch("password", "");

  useEffect(() => {
    const validatePassword = (password: string): PasswordValidationResults => {
      const results = passwordSchema.safeParse(password);
      const passwordErrors: PasswordValidationResults = {
        uppercase: false,
        length: false,
        lowercase: false,
        specialChar: false,
      };

      if (!results.success) {
        for (const error of results.error.errors) {
          const message = error.message;

          if (message === passwordCriteria.length) {
            passwordErrors.length = true;
          } else if (message === passwordCriteria.uppercase) {
            passwordErrors.uppercase = true;
          } else if (message === passwordCriteria.lowercase) {
            passwordErrors.lowercase = true;
          } else if (message === passwordCriteria.specialChar) {
            passwordErrors.specialChar = true;
          }
        }
      }

      return passwordErrors;
    };
    setPasswordValidationResults(validatePassword(password));

    const getPasswordStrength = (password: string) => {
      const hasLowercase = /[a-z]/.test(password);
      const hasUppercase = /[A-Z]/.test(password);
      const hasSpecialChar = /[\W_]/.test(password);

      let strength = 0;
      if (
        password.length > 10 &&
        password.length <= 12 &&
        hasLowercase &&
        hasSpecialChar &&
        hasUppercase
      )
        strength = 4;
      else if (
        password.length > 7 &&
        password.length <= 12 &&
        hasLowercase &&
        hasSpecialChar &&
        hasUppercase
      )
        strength = 3;
      else if (
        password.length > 5 &&
        password.length <= 12 &&
        ((hasLowercase && hasSpecialChar) ||
          (hasLowercase && hasUppercase) ||
          (hasSpecialChar && hasUppercase))
      )
        strength = 2;
      else if (
        password.length > 1 &&
        password.length <= 12 &&
        (hasLowercase || hasSpecialChar || hasUppercase)
      )
        strength = 1;

      return strength;
    };
    const strength = getPasswordStrength(password);
    setPasswordStrengthLevel(strength);
  }, [password]);

  // Function to determine the strength bar color
  const getStrengthBarColor = (level: number) => {
    switch (level) {
      case 1:
        return "bg-[#FF183D]";
      case 2:
        return "bg-[#FFF618]";
      case 3:
        return "bg-[#8FFF00]";
      case 4:
        return "bg-[#24FF00]";
      default:
        return "bg-gray-500";
    }
  };

  const getStrengthTextColor = (level: number) => {
    switch (level) {
      case 1:
        return "text-[#FF183D]";
      case 2:
        return "text-[#FFF618]";
      case 3:
        return "text-[#8FFF00]";
      case 4:
        return "text-[#24FF00]";
      default:
        return "text-gray-500";
    }
  };

  const navigate = useNavigate();

  const handleSave = () => {
    onSave();
  };

  const onSubmit: SubmitHandler<SignUpFormData> = async (data) => {
    const postData = {
      email,
      verifyToken: token,
      password: data.password,
      verifyPassword: data.confirmPassword,
      logoutAllDevices,
    };

    try {
      const response = await api.post(
        "/reset-password/resetPassword",
        postData,
      );
      console.log("Response: ", response);
      console.log("PostData: ", postData);
      toast.success("Password reset successful!");
      navigate("/");
      handleSave();
    } catch (error) {
      console.error("Failed to reset password: ", error);
      toast.error("Failed to reset password, please try again.");
      // alert("Failed to reset password, please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 `}>
        {/* Left Column for the Form */}
        <div className={`hidden h-full w-full md:block `}>
          <img
            src={loginBg}
            alt="CryptoDrop"
            className="object-fill w-full h-full rounded-lg"
          />
        </div>
        {/* Right Column for the Image */}
        <div className="flex flex-col justify-center w-full h-full p-2 text-white rounded-l-lg ">
          <div className="mb-5 font-['Inter'] text-sm font-normal text-white">
            Steps 02 / 02
          </div>
          <div className="mb-2 font-['Inter'] text-2xl font-medium text-white">
            Password Reset
          </div>
          <div className="mb-2 w-[393px] font-['Montserrat'] text-base font-normal leading-tight text-white">
            Type Your New Password Here.
          </div>

          {/* Password field */}
          <div className="mb-3">
            <label
              htmlFor="password"
              className="block mb-1 text-xs font-medium text-white sm:text-sm"
            >
              Password
            </label>
            <Input
              id="password"
              {...register("password")}
              type={showCurrentPassword ? "text" : "password"}
              endAdornment={
                <img
                  src={showCurrentPassword ? showpassword : hidepassword}
                  className="mr-3 cursor-pointer size-6"
                  onClick={() =>
                    setShowCurrentPassword((prevState) => !prevState)
                  }
                />
              }
              placeholder="Your password"
              className="w-full px-3 py-2 mt-1 text-white border-2 border-solid rounded-md border-white/5 bg-white/5 sm:text-sm"
            />
            {errors.password && (
              <p className="text-[10px] text-[#FF6780]">
                {errors.password.message}
              </p>
            )}

            {/* Password strength bar */}
            <div className="flex mt-2">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className={`ml-1 h-1 flex-1 rounded ${
                    index < passwordStrengthLevel
                      ? getStrengthBarColor(passwordStrengthLevel)
                      : "bg-white/5"
                  }`}
                ></div>
              ))}
            </div>
            <p
              className={`mt-1 text-sm ${getStrengthTextColor(passwordStrengthLevel)}`}
            >
              {" "}
              {
                [
                  " ",
                  "Weak Password",
                  "Moderate Password",
                  "Good Password",
                  "Strong Password",
                ][passwordStrengthLevel]
              }
            </p>

            {/* Password Validation Results */}
            <div className="mt-2">
              <div className="grid grid-rows-2">
                <div className="grid grid-cols-2">
                  <div
                    className={
                      !passwordValidationResults.uppercase
                        ? "flex items-center gap-2 text-xs text-green-500 "
                        : "flex items-center gap-2 text-xs text-gray-500"
                    }
                  >
                    {!passwordValidationResults.uppercase ? (
                      <img src={greenCheck} alt="checkuppercase" />
                    ) : (
                      <img src={defaultCheck} alt="checkuppercasedefault" />
                    )}{" "}
                    {passwordCriteria.uppercase}
                  </div>
                  <div
                    className={
                      !passwordValidationResults.specialChar
                        ? "flex items-center gap-2 text-xs text-green-500"
                        : "flex items-center gap-2 text-xs text-gray-500"
                    }
                  >
                    {!passwordValidationResults.specialChar ? (
                      <img src={greenCheck} alt="checkspecialChar" />
                    ) : (
                      <img src={defaultCheck} alt="checkspecialChardefault" />
                    )}{" "}
                    {passwordCriteria.specialChar}
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div
                    className={
                      !passwordValidationResults.lowercase
                        ? "flex items-center gap-2 text-xs text-green-500"
                        : "flex items-center gap-2 text-xs text-gray-500"
                    }
                  >
                    {!passwordValidationResults.lowercase ? (
                      <img src={greenCheck} alt="checklowercase" />
                    ) : (
                      <img src={defaultCheck} alt="checklowercasedefault" />
                    )}{" "}
                    {passwordCriteria.lowercase}
                  </div>

                  <div
                    className={
                      password.length >= 8 && password.length <= 12
                        ? "flex items-center gap-2 text-xs text-green-500"
                        : "flex items-center gap-2 text-xs text-gray-500"
                    }
                  >
                    {password.length >= 8 && password.length <= 12 ? (
                      <img src={greenCheck} alt="checklength" />
                    ) : (
                      <img src={defaultCheck} alt="checklengthdefault" />
                    )}{" "}
                    {passwordCriteria.length}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Confirm Password field */}
          <div className="mb-3">
            <label
              htmlFor="confirmPassword"
              className="block mb-1 text-xs font-medium text-white sm:text-sm"
            >
              Confirm Password
            </label>
            <Input
              id="confirmPassword"
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              endAdornment={
                <img
                  src={showConfirmPassword ? showpassword : hidepassword}
                  className="mr-3 cursor-pointer size-6"
                  onClick={() =>
                    setShowConfirmPassword((prevState) => !prevState)
                  }
                />
              }
              placeholder="Confirm password"
              className="w-full px-3 py-2 mt-1 text-white border-2 border-solid rounded-md border-white/5 bg-white/5 sm:text-sm"
            />
            {errors.confirmPassword && (
              <p className="text-[10px] text-[#FF6780]">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Terms of Service Checkbox */}
          <div className="flex items-center mb-3">
            <input
              type="checkbox"
              color="success"
              className="mt-[2px] text-white"
              checked={logoutAllDevices}
              onChange={(e: any) => setLogoutAllDevices(e.target.checked)}
            />
            <label
              htmlFor="termsOfService"
              className="ml-2 text-sm font-normal text-white"
            >
              Logout From All Devices
            </label>
          </div>

          {/* Submit button */}
          <Button
            type="submit"
            className="mt-3 w-full rounded-md bg-gradient-to-r from-[#003BD2] to-[#00FFC2]"
          >
            Save the Changes
          </Button>
        </div>
      </div>
    </form>
  );
};
